import { useEffect, useMemo } from 'react';
import { AccommodationsConsoleState } from '@apps/accommodations/AccommodationsConsole.types';
import { useAccommodationPlacesByCouple } from './useAccommodationPlacesByCouple';
import { useRegionalHotels } from './useRegionalHotels';
import { useBackgroundPricingRefresh } from './useBackgroundPricingRefresh';

type UseAccommodationsConsoleListingsArgs = Readonly<{
  state: AccommodationsConsoleState;
  variant: 'couple' | 'regional';
  skipCouplePicks?: boolean;
  onError?: (error: unknown) => void;
  onSuccess?: () => void;
  isInitialLoad: boolean;
  shouldPrioritizeParams?: boolean;
}>;

export const useAccommodationsConsoleListings = (args: UseAccommodationsConsoleListingsArgs) => {
  const { state, variant, skipCouplePicks = false, onError, onSuccess, isInitialLoad, shouldPrioritizeParams } = args;

  const isViewingCouplesPicks = variant === 'couple' && !skipCouplePicks;

  const { eventAccommodations, isFetchingEventAccommodations, errorFetchingAccommodationPrices } = useAccommodationPlacesByCouple({
    state,
    skip: skipCouplePicks,
  });

  const {
    payload,
    listings,
    cachedListings,
    isFetchingListings,
    error: regionalError,
  } = useRegionalHotels({
    state,
    isInitialLoad,
    shouldPrioritizeParams,
  });

  const {
    error: backgroundError,
    listings: backgroundListings,
    isLoading: isBackgroundPricingLoading,
  } = useBackgroundPricingRefresh({
    isFetchingListings,
    regionalHotelsPayload: payload,
  });

  const hasAnyError = Boolean(regionalError || backgroundError);
  const areQueriesSettled = !isFetchingListings && !isBackgroundPricingLoading;
  const shouldUseBackgroundListings = areQueriesSettled && !backgroundError && backgroundListings.length > 0;

  const consolidatedListings = useMemo(() => {
    const regionalListings = shouldUseBackgroundListings ? backgroundListings : cachedListings;

    if (skipCouplePicks) {
      return regionalListings;
    }

    const searchScoreByJoyPlaceId = new Map(regionalListings.filter(l => l.joyPlaceId).map(l => [l.joyPlaceId, l.searchScore]));

    const couplePickJoyPlaceIds = new Set<string>(eventAccommodations.filter(x => x.joyPlaceId).map(x => x.joyPlaceId) as string[]);

    const regionalListingsJoyPlaceIds = new Set<string>(regionalListings.filter(l => l.joyPlaceId).map(l => l.joyPlaceId as string));
    const couplesPickToShow = isInitialLoad
      ? eventAccommodations
      : eventAccommodations.filter(accommodation => accommodation.joyPlaceId && regionalListingsJoyPlaceIds.has(accommodation.joyPlaceId));

    return [
      ...couplesPickToShow.map(accommodation => ({
        ...accommodation,
        searchScore: accommodation.joyPlaceId ? searchScoreByJoyPlaceId.get(accommodation.joyPlaceId) : undefined,
        otaProvider: cachedListings.find(listing => listing.joyPlaceId === accommodation.joyPlaceId)?.otaProvider,
      })),
      ...regionalListings.filter(listing => !!listing.joyPlaceId && !couplePickJoyPlaceIds.has(listing.joyPlaceId)),
    ];
  }, [eventAccommodations, backgroundListings, cachedListings, skipCouplePicks, isInitialLoad, shouldUseBackgroundListings]);

  useEffect(() => {
    if (regionalError) {
      onError?.(regionalError);
    } else if (areQueriesSettled && listings.length > 0) {
      onSuccess?.();
    }
  }, [regionalError, areQueriesSettled, listings.length, onError, onSuccess]);

  return {
    cachedListings,
    listings: consolidatedListings,
    isFetchingListings: isFetchingListings || isFetchingEventAccommodations,
    isFetchingListingPrices: false,
    hasAnyError,
    isCombinedLoading: !areQueriesSettled,
    isUsingBackgroundListings: shouldUseBackgroundListings,
    pricingError: isViewingCouplesPicks ? errorFetchingAccommodationPrices : null,
  };
};
